/**
 * Home Page Component for FlowRide Frontend
 * Main page component that handles route planning, map display, and itinerary management.
 * Integrates search functionality, map visualization, and real-time vehicle tracking.
 */

import { IonContent, IonPage } from "@ionic/react";
import maplibregl from "maplibre-gl";
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type Dispatch,
    type SetStateAction,
} from "react";
import ReactDOMServer from "react-dom/server";
import { useHistory, useLocation } from "react-router-dom";
import CompassDebug from "../../components/Debug/CompassDebug";
import { useDynamicIslandOrchestrator } from "../../components/DynamicIsland";
import { FloatingMenu } from "../../components/FloatingActions";
import Header from "../../components/Header";
import { toggleSelectedItineraryLeg } from "../../components/ItinerariesPlanner/itineraryDetails/itineraryDetails.tools";
import ItineraryList from "../../components/ItinerariesPlanner/itineraryList/ItineraryList";
import ItineraryPanel from "../../components/ItinerariesPlanner/itineraryPanel/ItineraryPanel";
import { usePanelLayoutBounds } from "../../components/ItinerariesPlanner/panelResize";
import { MapComponent } from "../../components/MapLibre/MapComponent";
import NavigationTopPanel from "../../components/Navigation/NavigationTopPanel";
import SearchBarInput from "../../components/SearchBar/SearchBarInput";
import { SEARCH_BAR } from "../../config/global.configuration";
import { ITINERARY_PANEL } from "../../config/itineraryPanel.configuration";
import { useCarItinerary } from "../../hooks/CarItineraryHook";
import { useMapItinerary } from "../../hooks/MapItineraryHook";
import { useMapNavigation } from "../../hooks/MapNavigationHook";
import { useOffRouteRecalculation } from "../../hooks/OffRouteRecalculationHook";
import { usePageVisibility } from "../../hooks/usePageVisibility.hook";
import MaPositionIcon from "../../image/icons/Inputs/MyLocationIcon.png";
import DestinationIcon from "../../image/icons/map/details/DestinationIcon.svg";
import { VehicleManager } from "../../managers/VehicleManager";
import { ItineraryService } from "../../services/ItineraryService";
import { gpsPredictionService, RawGpsDataService, rawGpsDataService } from "../../services/navigation";
import { ephemeralStore, itineraryPanelStateSlice, navigationSessionSlice, resetPanelToExpandedTier, restoreRememberedItineraryDetailsSnapTier, restoreRememberedItineraryListSnapTier, useEphemeralSelector, useMapInstance } from "../../store/ephemeral";
import {
    displayCurrencyToIsEuro,
    fabUiSlice,
    mapPrefsSlice,
    pricingPrefsSlice,
    savedItinerariesSlice,
    usePersistedSlice,
} from "../../store/persistence";
import { useLayoutRef } from "../../store/shared/hooks/useLayoutRef";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { getGeocodeItemSubtitle } from "../../tools/GeocodeTools";
import { getUserLocation, subscribeToLocation } from "../../tools/GpsLocationAdapter";
import { DecodePath } from "../../tools/ItineraryDecoder";
import { calculateDistance, convertItineraryToSegments } from "../../tools/NavigationTools";
import { countFutureSavedItineraries, type SavedListFilterTabId } from "../../tools/savedItineraryDisplay.tools";
import { type ItineraryDTO, type ItineraryLegDTO } from "../../types/dtos/api/itinerary/itinerary.dto";
import { type DateWithMode, type Route } from "../../types/Forms";
import { GeocodeSource, GeocodeType, SearchResultItem, type DepartureAndArrival } from "../../types/geocode-ui.types";
import { type ItineraryListContentMode } from "../../types/ItineraryPlanner";
import { resolveActiveMapStyleId } from "../../types/MapStyle";
import { NavigationMode } from "../../types/Navigation";

/** DOM ids for the search pill rows (exclude autocomplete list height). */
const SEARCH_INPUT_ARRIVAL_ROW_ID = "SearchInputArrivalRow";
const SEARCH_INPUT_DEPARTURE_ROW_ID = "SearchInputDepartureRow";
const SEARCH_INPUT_ARRIVAL_WRAPPER_ID = "SearchInputArrival";
const SEARCH_INPUT_DEPARTURE_WRAPPER_ID = "SearchInputDeparture";

/**
 * Bottom Y (viewport px) of the visible search pill, ignoring the autocomplete
 * dropdown which lives inside the wrapper but below the row (stacking is fixed
 * with z-index on the search wrapper when suggestions are open).
 */
function searchBarChromeBottomPx( rowId: string, wrapperId: string ): number | null {
    const row = document.getElementById( rowId );
    if ( row !== null ) {
        const rect = row.getBoundingClientRect();
        if ( rect.height > 0 ) {
            return rect.bottom;
        }
    }
    const wrapper = document.getElementById( wrapperId );
    if ( wrapper === null ) {
        return null;
    }

    return wrapper.getBoundingClientRect().bottom;
}

/** Vehicle manager instance for handling real-time vehicle data */
const vehicleManager = new VehicleManager();

/**
 * Get arrival point icon JSX
 */
const getArrivalIconJSX = () => {
    return ( <img
        alt="ArrivalPoint"
        className="w-[20px]"
        src={DestinationIcon}
             /> );
};

const Home: React.FC = () => {
    const [ myPositionAutocomplete, setMyPositionAutocomplete ] = useState<SearchResultItem>( {
        "id":       "",
        "type":     GeocodeType.ADDRESS,
        "result":   "Ma position",
        "title":    "Ma position",
        "subtitle": "",
        "icon":     {
            "alt": "Ma position",
            "src": MaPositionIcon,
        },
        "resume":   "Ma position",
        "source":   GeocodeSource.MYLOCATION,
        "location": {
            "coordinates": {
                "lat": RawGpsDataService.getInstance().getLastValue().latitude,
                "lon": RawGpsDataService.getInstance().getLastValue().longitude,
            },
        },
    } );

    // Arrival fetched geocoding values
    const [ autocompleteValues, setAutocompleteValues ] = useState<SearchResultItem[]>( [] );

    // Departure fetched geocoding values
    const [ autocompleteDepartureValues, setAutocompleteDepartureValues ] = useState<
        SearchResultItem[]
    >( [] );

    /** Current pixel height of the bottom itinerary panel, used as the burst bottom safe margin. */
    const [ itineraryPanelHeightPx, setItineraryPanelHeightPx ] = useState<number>( 0 );

    /**
     * Absolute Y coordinate (in pixels from the viewport top) of the lowest
     * edge of the search **pills** (not the autocomplete list height). The list
     * is stacked above the floating menu via z-index on the search wrapper when
     * suggestions are open. `null` means the fields are not mounted yet.
     */
    const [ searchBarsBottomPx, setSearchBarsBottomPx ] = useState<number | null>( null );

    // Selected address from departure search input
    const [ departureSelectedAddress, setDepartureSelectedAddress ] =
        useState<SearchResultItem | null>( myPositionAutocomplete );

    // Selected address from arrival search input
    const [ arrivalSelectedAddress, setArrivalSelectedAddress ] =
        useState<SearchResultItem | null>( null );

    // Arrival Input value
    const [ arrivalInput, setArrivalInput ] = useState<string>( "" );

    // Departure Input value
    const [ departureInput, setDepartureInput ] = useState<string>( "" );

    // Swipeable container states
    const [ visible, setVisible ] = useState<boolean>( false );
    const [ itineraryListContentMode, setItineraryListContentMode ] = useState<ItineraryListContentMode>( "search" );
    const [ activeSavedFilterTab, setActiveSavedFilterTab ] = useState<SavedListFilterTabId>( "all" );

    /*
     * calendar states
     * date & time data with mode information
     */
    const [ date, setDate ] = useState<DateWithMode>( () => ( {
        "timestamp":            Date.now(),
        "isDepartureTimestamp": true,
    } ) );

    // Check if edited date or not
    const [ isEditedDate, setIsEditedDate ] = useState( false );

    const [ isMerge, setIsMerge ] = useState<boolean>( false );

    /**
     * Arrival field `top` (px) — derived from merge so it updates in the same
     * commit as `isMerge` (no deferred `setPosition`), keeping layout samples
     * aligned with the CSS `top` transition on the arrival wrapper.
     *
     * Non-merge: `106` = departure pill bottom (`top-12` + `h-12` = 96) + 10px gap,
     * so the arrival wrapper does not overlap the departure field (avoids
     * autocomplete backdrop bleed onto departure).
     */
    const arrivalFieldTopPx = useMemo( () => String( isMerge ? 12 : 106 ), [ isMerge ] );

    // Detail modal arrow top state
    const [ , setShowArrowTop ] = useState<boolean>( false );

    /*
     * Itinerary list container height (now managed globally via ephemeral context)
     * Selected itinerary to see on the map
     */
    const [ selectedItinerary, setSelectedItinerary ] = useState<ItineraryDTO | null>( null );
    const [ selectedLeg, setSelectedLeg ] = useState<ItineraryLegDTO | null>( null );
    const clearVehicleSelectionRef = useRef<( () => void ) | null>( null );

    /**
     * Highlights an itinerary leg for the transit-path overlay and clears any active vehicle selection.
     *
     * @param action Next leg value or updater (same contract as `setSelectedLeg`).
     */
    const setSelectedLegForTransitPath = useCallback<Dispatch<SetStateAction<ItineraryLegDTO | null>>>(
        ( action ) => {
            setSelectedLeg( ( previousLeg ) => {
                const nextLeg = typeof action === "function" ? action( previousLeg ) : action;

                if ( nextLeg !== null && ( previousLeg === null || previousLeg.id !== nextLeg.id ) ) {
                    queueMicrotask( () => {
                        clearVehicleSelectionRef.current?.();
                    } );
                }

                return nextLeg;
            } );
        },
        [],
    );

    /** When true, the next selectedLeg change should not trigger fitBounds (selection came from the map). */
    const skipFitBoundsForSelectedLegRef = useRef( false );

    // I take my car choice state
    const [ ITakeMyCar, setITakeMyCar ] = useState<boolean>( false );
    const [ ITakeMyCarData, setITakeMyCarData ] = useState<ItineraryDTO | null>( null );

    // Compass debug state (temporarily hidden)
    const [ showCompassDebug, setShowCompassDebug ] = useState( false );
    const [ selectedCarRouteId, setSelectedCarRouteId ] = useState<string>( "" );
    const [ carItineraries, setCarItineraries ] = useState<Route[]>( [] );
    const [ goItineraries, setGoItineraries ] = useState<Route[]>( [] );
    const [ backupReturnItineraries, setBackupReturnItineraries ] = useState<Route[]>( [] );

    // Itinerary list state
    const [ itineraries, setItineraries ] = useState<ItineraryDTO[]>( [] );

    /**
     * Planner coordinates derived from the selected geocode items.
     *
     * @remarks Departure uses `-1` as the "current GPS position" sentinel when no explicit
     * departure is selected. Arrival uses `0` while unset.
     */
    const selectedInputs = useMemo<DepartureAndArrival>( () => {
        return {
            "departure": {
                "lat": departureSelectedAddress?.location?.coordinates?.lat
                    ? Number( departureSelectedAddress.location.coordinates.lat )
                    : -1,
                "lon": departureSelectedAddress?.location?.coordinates?.lon
                    ? Number( departureSelectedAddress.location.coordinates.lon )
                    : -1,
            },
            "arrival": {
                "lat": arrivalSelectedAddress?.location?.coordinates?.lat
                    ? Number( arrivalSelectedAddress.location.coordinates.lat )
                    : 0,
                "lon": arrivalSelectedAddress?.location?.coordinates?.lon
                    ? Number( arrivalSelectedAddress.location.coordinates.lon )
                    : 0,
            },
            "departureAddress": departureSelectedAddress ?? undefined,
            "arrivalAddress":   arrivalSelectedAddress ?? undefined,
        };
    }, [ arrivalSelectedAddress, departureSelectedAddress ] );

    // Function to parse or update query params
    const location = useLocation();
    const history = useHistory(); // ou useNavigate() si tu es en v6
    const map = useMapInstance();
    const dragHeightVh = useEphemeralSelector( itineraryPanelStateSlice, ( panel ) => panel.dragHeightVh );
    const panelSnapTier = useEphemeralSelector( itineraryPanelStateSlice, ( panel ) => panel.panelSnapTier );
    const itineraryListHeightCommitted = useEphemeralSelector( itineraryPanelStateSlice, ( panel ) => panel.itineraryListHeight );
    const isDragging = useEphemeralSelector( itineraryPanelStateSlice, ( panel ) => panel.isDragging );
    const { panelResizeLayout, maximizedPanelHeightVh, containerRef } = usePanelLayoutBounds( {
        "panelTopGapPx":  ITINERARY_PANEL.panelMaximizedTopGapPx,
        "layoutRevision": searchBarsBottomPx,
    } );
    const effectivePanelHeightVh = useMemo( () => {
        if ( dragHeightVh !== null ) {
            return dragHeightVh;
        }

        if ( panelSnapTier === "maximized" ) {
            return maximizedPanelHeightVh;
        }

        return itineraryListHeightCommitted;
    }, [ dragHeightVh, maximizedPanelHeightVh, panelSnapTier, itineraryListHeightCommitted ] );
    const isItineraryListPanel = selectedItinerary === null && !ITakeMyCar;
    const shouldLockPanelHeightToSnap = isDragging || isItineraryListPanel;
    const panelContainerTransitionClass = isDragging
        ? ""
        : shouldLockPanelHeightToSnap
            ? "transition-[height,max-height] duration-300 ease-out"
            : "transition-[max-height] duration-300 ease-out";
    const maximizedPanelHeightVhRef = useLayoutRef( maximizedPanelHeightVh );

    const resetPanelExpanded = useCallback( (): void => {
        resetPanelToExpandedTier( maximizedPanelHeightVhRef.current );
    }, [ maximizedPanelHeightVhRef ] );
    const restoreItineraryDetailsPanelSnap = useCallback( (): void => {
        restoreRememberedItineraryDetailsSnapTier( maximizedPanelHeightVhRef.current );
    }, [ maximizedPanelHeightVhRef ] );
    const restoreItineraryListPanelSnap = useCallback( (): void => {
        restoreRememberedItineraryListSnapTier( maximizedPanelHeightVhRef.current );
    }, [ maximizedPanelHeightVhRef ] );

    const { "nowMs": savedItinerariesNowMs } = usePageVisibility( {
        "trackNow":       true,
        "tickIntervalMs": SEARCH_BAR.refreshTimeIntervalMs,
    } );
    const [ savedItineraries ] = usePersistedSlice( savedItinerariesSlice );
    const futureSavedItineraryCount = useMemo( () => {
        return countFutureSavedItineraries( savedItineraries, savedItinerariesNowMs );
    }, [
        savedItineraries,
        savedItinerariesNowMs,
    ] );

    /**
     * Opens the itinerary panel in saved-itinerary list mode.
     */
    const handleOpenSavedItineraries = useCallback( (): void => {
        setSelectedItinerary( null );
        setActiveSavedFilterTab( "all" );
        setItineraryListContentMode( "saved" );
        setVisible( true );
        resetPanelExpanded();
    }, [ resetPanelExpanded ] );

    /**
     * Clears the arrival search and hides the results panel (same effect as the arrival field close control).
     */
    const handleCloseSearchResults = useCallback( (): void => {
        setArrivalInput( "" );
        setArrivalSelectedAddress( null );
        setSelectedItinerary( null );
        resetPanelExpanded();
        setVisible( false );
        setITakeMyCarData( null );
    }, [
        resetPanelExpanded,
        setArrivalInput,
        setArrivalSelectedAddress,
        setITakeMyCarData,
        setSelectedItinerary,
        setVisible,
    ] );
    const navigationMode = useEphemeralSelector( navigationSessionSlice, ( session ) => session.navigationMode );
    const isFollowingNavigation = useEphemeralSelector( navigationSessionSlice, ( session ) => session.isFollowingNavigation );
    const isFollowingVehicle = useEphemeralSelector( navigationSessionSlice, ( session ) => session.isFollowingVehicle );
    const setIsFollowingNavigation = useCallback( ( following: boolean ): void => {
        ephemeralStore.setValue( navigationSessionSlice, ( previous ) => ( {
            ...previous,
            "isFollowingNavigation": following,
            "isFollowingVehicle":    following ? false : previous.isFollowingVehicle,
        } ) );
    }, [] );

    const toggleVehicleFollow = useCallback( (): void => {
        ephemeralStore.setValue( navigationSessionSlice, ( previous ) => {
            const nextFollowingVehicle = !previous.isFollowingVehicle;

            return {
                ...previous,
                "isFollowingVehicle":    nextFollowingVehicle,
                "isFollowingNavigation": nextFollowingVehicle ? false : previous.isFollowingNavigation,
            };
        } );
    }, [] );

    const { updateCamera } = useMapNavigation( { "handleAnimation": false } );

    const queryParams = useMemo( () => {
        return new URLSearchParams( location.search );
    }, [ location.search ] );

    const [ mapPrefs, setMapPrefs ] = usePersistedSlice( mapPrefsSlice );
    const [ pricingPrefs ] = usePersistedSlice( pricingPrefsSlice );
    const [ fabUiPrefs, setFabUiPrefs ] = usePersistedSlice( fabUiSlice );
    const debugItineraries = fabUiPrefs.debugItineraries === true;
    const mapAppearanceMode = mapPrefs.appearanceMode;
    const mapStyleOverride = mapPrefs.styleOverride;
    const [ userLocation, setUserLocation ] = useState( getUserLocation() );
    const [ systemPrefersDark, setSystemPrefersDark ] = useState<boolean>( () => {
        if ( typeof window === "undefined" ) {
            return false;
        }

        return window.matchMedia( "(prefers-color-scheme: dark)" ).matches;
    } );

    // Derived state from NavigationMode (replaces old trackingMode)
    const isNavigating = useMemo( () => {
        return  navigationMode === NavigationMode.CONSTRAINED_NAVIGATION;
    }, [ navigationMode ] );

    const mapStyleId = useMemo( () => {
        return resolveActiveMapStyleId( mapAppearanceMode, mapStyleOverride, systemPrefersDark );
    }, [ mapAppearanceMode, mapStyleOverride, systemPrefersDark ] );

    const defaultMapStyleId = useMemo( () => {
        return resolveActiveMapStyleId( mapAppearanceMode, "default", systemPrefersDark );
    }, [ mapAppearanceMode, systemPrefersDark ] );

    const isDarkUi = useMemo( () => {
        if ( mapAppearanceMode === "auto" ) {
            return systemPrefersDark;
        }

        return mapAppearanceMode === "dark";
    }, [ mapAppearanceMode, systemPrefersDark ] );
    const uiPalette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );

    /** Clears the itinerary transit-path overlay when a realtime vehicle is selected. */
    const handleClearItineraryTransitPath = useCallback( (): void => {
        setSelectedLeg( null );
    }, [] );

    const handleTripLegClick = useCallback( ( leg: ItineraryLegDTO ): void => {
        toggleSelectedItineraryLeg( selectedLeg, leg, setSelectedLegForTransitPath );
    }, [ selectedLeg, setSelectedLegForTransitPath ] );

    const dynamicIsland = useDynamicIslandOrchestrator( {
        isDarkUi,
        updateCamera,
        selectedItinerary,
        "carItineraryData":    ITakeMyCarData,
        selectedLeg,
        "onItineraryLegClick": handleTripLegClick,
        isFollowingVehicle,
        toggleVehicleFollow,
    } );

    // Navigation services are now managed by useNavigationServices hook

    useEffect( () => {
        const mediaQuery = window.matchMedia( "(prefers-color-scheme: dark)" );
        const handleSystemThemeChange = ( event: MediaQueryListEvent ) => {
            setSystemPrefersDark( event.matches );
        };

        mediaQuery.addEventListener( "change", handleSystemThemeChange );

        return () => {
            mediaQuery.removeEventListener( "change", handleSystemThemeChange );
        };
    }, [] );

    // Toggle navigation mode (replaces switchTrackingMode)
    const toggleNavigationMode = useCallback( () => {
        /*
         * console.log( "🚀 [Home] toggleNavigationMode called", {
         *     isNavigating,
         *     selectedItinerary,
         *     "timestamp": new Date().toISOString(),
         * } );
         */
        const changeCameraAngle = !isFollowingNavigation;
        console.log( "[HOME] is following navigation:" + isFollowingNavigation );

        setIsFollowingNavigation( changeCameraAngle );
        console.log( navigationMode, changeCameraAngle, selectedItinerary );

        if ( selectedItinerary !== null ) {
            // Convert itinerary to route segments
            const segments = convertItineraryToSegments( selectedItinerary );

            if ( segments.length > 0 ) {
                gpsPredictionService.setRouteSegments( segments );
            }
        }
    }, [ selectedItinerary, setIsFollowingNavigation, isFollowingNavigation, navigationMode ] );

    // Subscribe to GPS location updates
    useEffect( () => {
        const unsubscribe = subscribeToLocation( setUserLocation );

        return unsubscribe;
    }, [] );

    /*
     * Observe the live pixel height of the bottom itinerary panel so the floating
     * action burst can reserve the right amount of safe area above it.
     * ResizeObserver reacts to both user-driven resizes and programmatic ones
     * (e.g. when an itinerary is selected and the panel expands).
     */
    useEffect( () => {
        if ( typeof window === "undefined" ) {
            return;
        }

        const element = document.getElementById( "itinerary-panel-root" );
        if ( element === null ) {
            return;
        }

        const updateHeight = (): void => {
            setItineraryPanelHeightPx( element.getBoundingClientRect().height );
        };

        updateHeight();

        if ( typeof ResizeObserver === "undefined" ) {
            return;
        }

        const observer = new ResizeObserver( () => {
            updateHeight();
        } );
        observer.observe( element );

        return () => {
            observer.disconnect();
        };
    }, [ effectivePanelHeightVh ] );

    /**
     * True when a search autocomplete dropdown is currently displaying results.
     * The grouped floating action burst fades out and auto-closes in that case.
     */
    const isSearchAutocompleteOpen = useMemo<boolean>( () => {
        return autocompleteDepartureValues.length > 0 || autocompleteValues.length > 0;
    }, [ autocompleteDepartureValues.length, autocompleteValues.length ] );

    /*
     * Track the bottom Y coordinate of the search fields so the floating
     * action menu can follow them (especially when toggling merge mode,
     * which repositions the arrival field). The measurement is driven by:
     *   - A `ResizeObserver` on the search wrappers (merge, pill size, etc.).
     *   - `transitionstart` / `transitionend` on the arrival field's `top`
     *     plus a `requestAnimationFrame` loop: `ResizeObserver` does not fire
     *     for pure `top` motion, so we sample every frame while the bar moves
     *     (see `SEARCH_BAR.topLayoutTransitionMs`).
     *   - Direct re-runs via React state dependencies (`isMerge`, arrival top).
     *   - A window resize listener for responsive layout changes.
     */
    useLayoutEffect( () => {
        if ( typeof window === "undefined" ) {
            return;
        }

        const recompute = (): void => {
            const bottoms: number[] = [];
            const arrivalBottom = searchBarChromeBottomPx( SEARCH_INPUT_ARRIVAL_ROW_ID, SEARCH_INPUT_ARRIVAL_WRAPPER_ID );
            const departureBottom = searchBarChromeBottomPx( SEARCH_INPUT_DEPARTURE_ROW_ID, SEARCH_INPUT_DEPARTURE_WRAPPER_ID );
            if ( arrivalBottom !== null ) {
                bottoms.push( arrivalBottom );
            }
            if ( departureBottom !== null ) {
                bottoms.push( departureBottom );
            }
            if ( bottoms.length === 0 ) {
                setSearchBarsBottomPx( null );

                return;
            }
            setSearchBarsBottomPx( Math.max( ...bottoms ) );
        };

        recompute();

        const arrival = document.getElementById( "SearchInputArrival" );
        const departure = document.getElementById( "SearchInputDeparture" );

        let rafId: number | null = null;
        let safetyTimer: number | null = null;

        const stopTopTransitionSampling = (): void => {
            if ( rafId !== null ) {
                cancelAnimationFrame( rafId );
                rafId = null;
            }
            if ( safetyTimer !== null ) {
                clearTimeout( safetyTimer );
                safetyTimer = null;
            }
        };

        const runTopTransitionSampling = (): void => {
            stopTopTransitionSampling();
            const tick = (): void => {
                recompute();
                rafId = window.requestAnimationFrame( tick );
            };
            rafId = window.requestAnimationFrame( tick );
            safetyTimer = window.setTimeout( () => {
                stopTopTransitionSampling();
                recompute();
            }, SEARCH_BAR.topLayoutTransitionMs + 80 );
        };

        const handleTransitionStart = ( event: TransitionEvent ): void => {
            if ( event.propertyName === "top" ) {
                runTopTransitionSampling();
            }
        };

        const handleTransitionEnd = ( event: TransitionEvent ): void => {
            if ( event.propertyName === "top" ) {
                stopTopTransitionSampling();
                recompute();
            }
        };
        if ( arrival !== null ) {
            arrival.addEventListener( "transitionstart", handleTransitionStart );
            arrival.addEventListener( "transitionend", handleTransitionEnd );
        }

        const handleWindowResize = (): void => {
            recompute();
        };
        window.addEventListener( "resize", handleWindowResize );

        let observer: ResizeObserver | null = null;
        if ( typeof ResizeObserver !== "undefined" ) {
            observer = new ResizeObserver( () => {
                recompute();
            } );
            if ( arrival !== null ) {
                observer.observe( arrival );
            }
            if ( departure !== null ) {
                observer.observe( departure );
            }
        }

        return () => {
            stopTopTransitionSampling();
            if ( arrival !== null ) {
                arrival.removeEventListener( "transitionstart", handleTransitionStart );
                arrival.removeEventListener( "transitionend", handleTransitionEnd );
            }
            window.removeEventListener( "resize", handleWindowResize );
            if ( observer !== null ) {
                observer.disconnect();
            }
        };
    }, [
        arrivalFieldTopPx,
        isMerge,
        userLocation.latitude,
        userLocation.longitude,
    ] );

    // Subscribe to user location
    useEffect( () => {
        const itineraryService = new ItineraryService();
        const MIN_DISTANCE_METERS = 50;

        const unsubscribe = subscribeToLocation( ( location ) => {
            const currentCoords = myPositionAutocomplete.location.coordinates;

            // Check if we need to update (initial position or distance > 50m)
            const isInitialPosition = currentCoords.lat === -1 && currentCoords.lon === -1;
            const dist = calculateDistance( currentCoords.lat, currentCoords.lon, location.latitude, location.longitude );
            const shouldUpdate = isInitialPosition || dist > MIN_DISTANCE_METERS;

            if ( shouldUpdate ) {
                /**
                 * Helper to build the updated myPositionAutocomplete from a geocode result.
                 * Uses actual GPS coordinates so departure is always accurate.
                 */
                const buildMyPosition = ( result: SearchResultItem ): SearchResultItem => ( {
                    ...result,
                    "source":   GeocodeSource.MYLOCATION,
                    "title":    "Ma position",
                    "subtitle": getGeocodeItemSubtitle( result ),
                    "icon":     {
                        "alt": "Ma position",
                        "src": MaPositionIcon,
                    },
                    "resume":   "Ma position",
                    "location": {
                        ...result.location,
                        "coordinates": {
                            "lat": location.latitude,
                            "lon": location.longitude,
                        },
                    },
                } );

                void itineraryService.
                    reverseGeocode( location.latitude, location.longitude, GeocodeType.ADDRESS ).
                    then( async( geocodeResults ) => {
                        // Fallback to ALL type if ADDRESS returned no results
                        let results = geocodeResults;
                        if ( results.length === 0 ) {
                            results = await itineraryService.reverseGeocode( location.latitude, location.longitude );
                        }

                        if ( results.length > 0 ) {
                            const result = results[0];
                            result.source = GeocodeSource.MYLOCATION;
                            const updatedPosition = buildMyPosition( result );
                            setMyPositionAutocomplete( updatedPosition );
                        } else {
                            // Even with no geocode results, update coordinates to real GPS position
                            setMyPositionAutocomplete( ( prev ) => ( {
                                ...prev,
                                "location": {
                                    ...prev.location,
                                    "coordinates": {
                                        "lat": location.latitude,
                                        "lon": location.longitude,
                                    },
                                },
                            } ) );
                        }
                    } ).
                    catch( ( error ) => {
                        console.error( "[Home] Reverse geocoding error:", error );

                        // Even on error, update coordinates to real GPS position
                        setMyPositionAutocomplete( ( prev ) => ( {
                            ...prev,
                            "location": {
                                ...prev.location,
                                "coordinates": {
                                    "lat": location.latitude,
                                    "lon": location.longitude,
                                },
                            },
                        } ) );
                    } );
            }
        } );

        return unsubscribe;
    }, [ myPositionAutocomplete ] );

    const DepartureInputRef = useRef<HTMLInputElement>( null );
    const ArrivalInputRef = useRef<HTMLInputElement>( null );

    // Reference to store the arrival marker
    const arrivalMarkerRef = useRef<maplibregl.Marker | null>( null );
    const lastArrivalAutoCenterKeyRef = useRef<string | null>( null );

    const [ , setResetMap ] = useState( false );

    // Function to handle reset
    const handleResetMap = () => {
        setSelectedLeg( null ); // Clear the selected leg
        setResetMap( true ); // Trigger reset
    };

    const [ isModalOpen, setIsModalOpen ] = useState( false );
    const [ isPricingPrefsPanelOpen, setIsPricingPrefsPanelOpen ] = useState( false );
    const isEuro = displayCurrencyToIsEuro( pricingPrefs.displayCurrency );
    const setMapAppearanceMode = useCallback( ( mode: typeof mapAppearanceMode ): void => {
        setMapPrefs( ( previous ) => ( {
            ...previous,
            "appearanceMode": mode,
        } ) );
    }, [ setMapPrefs ] );
    const setMapStyleOverride = useCallback( ( override: typeof mapStyleOverride ): void => {
        setMapPrefs( ( previous ) => ( {
            ...previous,
            "styleOverride": override,
        } ) );
    }, [ setMapPrefs ] );

    const handleTransportsPrefsPanelOpenStateChange = useCallback( ( isOpen: boolean ) => {
        setIsModalOpen( isOpen );
    }, [] );

    const handlePricingPrefsPanelOpenStateChange = useCallback( ( isOpen: boolean ) => {
        setIsPricingPrefsPanelOpen( isOpen );
    }, [] );

    /**
     * Handle swapping departure and arrival values
     * 
     * Swaps all related state including inputs, selected addresses, and autocomplete values.
     * Clears itinerary data to trigger a new search.
     */
    const handleSwapDepartureArrival = useCallback( () => {
        // Swap input values
        const tempInput = departureInput;
        setDepartureInput( arrivalInput );
        setArrivalInput( tempInput );

        // Swap selected addresses
        const tempAddress = departureSelectedAddress;
        setDepartureSelectedAddress( arrivalSelectedAddress );
        setArrivalSelectedAddress( tempAddress );

        // Swap autocomplete values
        const tempAutocomplete = autocompleteDepartureValues;
        setAutocompleteDepartureValues( autocompleteValues );
        setAutocompleteValues( tempAutocomplete );

        // Clear itinerary data to trigger new search
        setSelectedItinerary( null );
        setITakeMyCarData( null );
        setItineraries( [] );
        setCarItineraries( [] );
        setSelectedCarRouteId( "" );
        setGoItineraries( [] );
        setBackupReturnItineraries( [] );
    }, [
        departureInput,
        arrivalInput,
        departureSelectedAddress,
        arrivalSelectedAddress,
        autocompleteDepartureValues,
        autocompleteValues,
    ] );

    const handleLegSelectedFromMap = useCallback( () => {
        skipFitBoundsForSelectedLegRef.current = true;
    }, [] );

    const focusMapOnPoint = useCallback( ( point: {
        "lat": number;
        "lon": number;
    } ) => {
        if ( map === undefined ) {
            return;
        }
        if ( Number.isFinite( point.lat ) === false || Number.isFinite( point.lon ) === false ) {
            return;
        }

        setSelectedLeg( null );

        // Build a tiny bounds box so fitBounds applies the same visible-area padding logic.
        const epsilon = 0.00005;
        const bounds = new maplibregl.LngLatBounds(
            [ point.lon - epsilon, point.lat - epsilon ],
            [ point.lon + epsilon, point.lat + epsilon ],
        );

        map.fitBounds( bounds, {
            "padding": {
                "top":    120,
                "bottom": ( effectivePanelHeightVh * window.innerHeight ) / 100 + 60,
                "left":   60,
                "right":  60,
            },
            "maxZoom":  16,
            "duration": 700,
        } );
    }, [ map, effectivePanelHeightVh ] );

    /** Dessine les itinéraires sur la carte */
    useMapItinerary( {
        "itinerary":            selectedItinerary,
        "itineraryListHeight":  effectivePanelHeightVh,
        "selectedLeg":          selectedLeg,
        "setSelectedLeg":       setSelectedLegForTransitPath,
        "isDarkUi":             isDarkUi,
        "onLegSelectedFromMap": handleLegSelectedFromMap,
    } );

    /**
     * Zoom map on selected leg from itinerary details (not when the user tapped the leg on the map).
     */
    useEffect( () => {
        if ( map === undefined || selectedLeg === null ) {
            return;
        }

        if ( skipFitBoundsForSelectedLegRef.current ) {
            skipFitBoundsForSelectedLegRef.current = false;

            return;
        }

        if ( selectedLeg.geometry?.path === undefined ) {
            return;
        }

        const decodedPath = DecodePath(
            selectedLeg.geometry.path,
            selectedLeg.geometry.type,
            selectedLeg.geometry.precision,
        );

        if ( decodedPath.length === 0 ) {
            return;
        }

        const bounds = new maplibregl.LngLatBounds(
            [ decodedPath[0].lng, decodedPath[0].lat ],
            [ decodedPath[0].lng, decodedPath[0].lat ],
        );

        decodedPath.forEach( ( point ) => {
            bounds.extend( [ point.lng, point.lat ] );
        } );

        map.fitBounds( bounds, {
            "padding": {
                "top":    120,
                "bottom": ( effectivePanelHeightVh * window.innerHeight ) / 100 + 60,
                "left":   60,
                "right":  60,
            },
            "maxZoom":  16,
            "duration": 700,
        } );
    }, [ map, selectedLeg, effectivePanelHeightVh ] );

    useCarItinerary( {
        "itineraries":         carItineraries,
        selectedCarRouteId,
        setSelectedCarRouteId,
        "itineraryListHeight": effectivePanelHeightVh,
        "legIdForSelected":    ITakeMyCarData?.legs?.[0]?.id,
    } );

    // Off-route recalculation hook
    const handleItineraryRecalculated = useCallback( ( newItinerary: ItineraryDTO ) => {
        setSelectedItinerary( newItinerary );
        setItineraries( [ newItinerary ] ); // Update itineraries with new itinerary
    }, [] );

    useOffRouteRecalculation( {
        "currentItinerary":        selectedItinerary,
        "onItineraryRecalculated": handleItineraryRecalculated,
        "enabled":                 isNavigating && selectedItinerary !== null,
        "offRouteTimeout":         30000, // 30 seconds
    } );

    // Sync departure selection into URL query params (session state, not persisted).
    useEffect( () => {
        if ( departureSelectedAddress !== null ) {
            queryParams.set( "s", departureSelectedAddress.result );
            queryParams.set( "slo", `${ departureSelectedAddress.location.coordinates.lon }` );
            queryParams.set( "sla", `${ departureSelectedAddress.location.coordinates.lat }` );
        } else {
            queryParams.delete( "s" );
            queryParams.delete( "slo" );
            queryParams.delete( "sla" );
        }
        const newSearch = `?${ queryParams.toString() }`;

        if ( location.search !== newSearch ) {
            history.replace( {
                ...location,
                "search": newSearch,
            } );
        }
    }, [ departureSelectedAddress, history, location, queryParams ] );

    /**
     * Update arrival marker and center map when arrival address is selected
     */
    useEffect( () => {
        if ( arrivalSelectedAddress === null || map === undefined ) {
            // Remove marker if address is cleared
            if ( arrivalMarkerRef.current !== null ) {
                arrivalMarkerRef.current.remove();
                arrivalMarkerRef.current = null;
            }
            lastArrivalAutoCenterKeyRef.current = null;

            return;
        }

        const coordinates = arrivalSelectedAddress.location?.coordinates;
        if ( coordinates === undefined || coordinates.lat === undefined || coordinates.lon === undefined ) {
            return;
        }

        const lat = coordinates.lat;
        const lon = coordinates.lon;

        // Remove existing marker if it exists
        if ( arrivalMarkerRef.current !== null ) {
            arrivalMarkerRef.current.remove();
            arrivalMarkerRef.current = null;
        }

        // Create arrival marker icon
        const arrivalIconHtml = ReactDOMServer.renderToStaticMarkup( getArrivalIconJSX() );
        const arrivalMarkerEl = document.createElement( "div" );
        arrivalMarkerEl.innerHTML = arrivalIconHtml;

        // Create and add marker to map
        const arrivalMarker = new maplibregl.Marker( { "element": arrivalMarkerEl } ).
            setLngLat( [ lon, lat ] ).
            addTo( map );
        arrivalMarkerRef.current = arrivalMarker;

        const arrivalCenterKey = `${ arrivalSelectedAddress.id ?? "" }::${ lon.toFixed( 6 ) }::${ lat.toFixed( 6 ) }`;
        if ( lastArrivalAutoCenterKeyRef.current === arrivalCenterKey ) {
            return;
        }

        lastArrivalAutoCenterKeyRef.current = arrivalCenterKey;

        // Center map on arrival point only when arrival changes, not on map/style reinit.
        updateCamera( {
            "center":   [ lon, lat ],
            "zoom":     15,
            "duration": 800,
        } );
    }, [ arrivalSelectedAddress, map, updateCamera ] );

    /**
     * Clear all states when cancel one of searches (To Add each time we have things to refresh in future)
     */
    useEffect( () => {
        const hasArrival = arrivalInput.length > 0;
        const hasDeparture = departureInput.length > 0 || departureSelectedAddress !== null;

        if ( !hasArrival || !hasDeparture ) {
            queueMicrotask( () => {
                setITakeMyCar( false );
                setITakeMyCarData( null );
                setSelectedCarRouteId( "" );
                setCarItineraries( [] );
                setItineraries( [] );
                if ( itineraryListContentMode !== "saved" ) {
                    setVisible( false );
                }
            } );
        }
    }, [ arrivalInput, departureInput, departureSelectedAddress, itineraryListContentMode ] );

    /**
     * Recalcul la dimension de la map en cas de changement de layout de la page, est-ce encore utile ???
     */
    useLayoutEffect( () => {
        setTimeout( () => {
            if ( map !== undefined ) {
                map.resize();
            }
        }, 100 );
    }, [ map ] );

    const returnToUserLocation = useCallback( () => {
        ephemeralStore.setValue( navigationSessionSlice, ( previous ) => {
            if ( !previous.isFollowingVehicle ) {
                return previous;
            }

            return {
                ...previous,
                "isFollowingVehicle": false,
            };
        } );

        updateCamera( {
            "center":   [ rawGpsDataService.getLastValue().longitude, rawGpsDataService.getLastValue().latitude ],
            "zoom":     16,
            "duration": 1200,
            "pitch":    0,
            "bearing":  0,
        } );
    }, [ updateCamera ] );

    const resetPitch = useCallback( () => {
        updateCamera( {
            "pitch":    0,
            "bearing":  0,
            "duration": 500,
        } );
    }, [ updateCamera ] );

    const handleNavigate = useCallback( () => {
        // iOS 13+ requires explicit permission for device orientation events.
        const DeviceOrientationEventTyped =
            DeviceOrientationEvent as unknown as { "requestPermission"?: () => Promise<"granted" | "denied"> };

        if (
            typeof DeviceOrientationEvent !== "undefined" &&
            typeof DeviceOrientationEventTyped.requestPermission === "function"
        ) {
            DeviceOrientationEventTyped.requestPermission().
                then( ( response: "granted" | "denied" ) => {
                    if ( response === "granted" ) {
                        if ( isNavigating ) {
                            resetPitch();
                        }
                        toggleNavigationMode();
                    }
                } ).
                catch( console.error );
        } else {
            if ( isNavigating ) {
                resetPitch();
            }
            toggleNavigationMode();
        }
    }, [ isNavigating, resetPitch, toggleNavigationMode ] );

    const handleShowCompassDebug = useCallback( () => {
        setShowCompassDebug( ( previousValue ) => !previousValue );
    }, [] );

    /**
     * Persists the debug toggle that enables itinerary debug helpers in itinerary views.
     *
     * @param value Next toggle value from the debug options MiniModal.
     */
    const handleDebugItinerariesChange = useCallback( ( value: boolean ): void => {
        setFabUiPrefs( ( previous ) => ( {
            ...previous,
            "debugItineraries": value,
        } ) );
    }, [ setFabUiPrefs ] );

    const handleZoomIn = useCallback( () => {
        if ( map === undefined ) {
            return;
        }

        updateCamera( { "zoom": map.getZoom() + 1 } );
    }, [ map, updateCamera ] );

    const handleZoomOut = useCallback( () => {
        if ( map === undefined ) {
            return;
        }

        updateCamera( { "zoom": map.getZoom() - 1 } );
    }, [ map, updateCamera ] );

    /**
     * Applies an arrival picked from the map context menu and starts planner search.
     *
     * @remarks Mirrors the arrival branch of {@link SearchBarInput} `handleAddress` so map
     * and autocomplete triggers stay aligned (merge mode, default departure, panel visible,
     * search content mode).
     *
     * @param item Geocoded arrival selected on the map.
     */
    const handleMapArrivalSelection = useCallback( ( item: SearchResultItem ) => {
        setArrivalSelectedAddress( item );
        setArrivalInput( item.resume );

        if ( departureInput === "" || departureSelectedAddress === null ) {
            setDepartureInput( myPositionAutocomplete.resume );
            setDepartureSelectedAddress( myPositionAutocomplete );
        }

        setIsMerge( true );

        // Leave saved-list mode before the panel remounts so OD search is not skipped.
        setItineraryListContentMode( "search" );
        setVisible( true );
        setSelectedItinerary( null );
        setITakeMyCarData( null );
        resetPanelExpanded();
        setCarItineraries( [] );
        setSelectedCarRouteId( "" );
        setGoItineraries( [] );
        setBackupReturnItineraries( [] );
    }, [
        departureInput,
        departureSelectedAddress,
        myPositionAutocomplete,
        setBackupReturnItineraries,
        setCarItineraries,
        setGoItineraries,
        resetPanelExpanded,
    ] );

    return (
        <IonPage>
            <IonContent forceOverscroll={false}>
                <div className="absolute left-0 right-0 bottom-0 top-0">
                    {isNavigating && (
                        <NavigationTopPanel itinerary={selectedItinerary ?? ITakeMyCarData} />
                    )}
                    <MapComponent
                        clearVehicleSelectionRef={clearVehicleSelectionRef}
                        debugItineraries={debugItineraries}
                        isDarkUi={isDarkUi}
                        isMerge={isMerge}
                        mapStyleId={mapStyleId}
                        onClearItineraryTransitPath={handleClearItineraryTransitPath}
                        onDebugItinerariesChange={handleDebugItinerariesChange}
                        onSetArrival={handleMapArrivalSelection}
                        onSetDeparture={( item ) => {
                            setDepartureSelectedAddress( item );
                            setDepartureInput( item.resume );
                        }}
                        onShowCompassDebug={handleShowCompassDebug}
                        onVehicleSelectionInfoChange={dynamicIsland.handleVehicleSelectionInfoChange}
                        onZoomIn={handleZoomIn}
                        onZoomOut={handleZoomOut}
                        vehicleManager={vehicleManager}
                    />
                    {/* Compass Debug Overlay (temporarily hidden) */}
                    {showCompassDebug && (
                        <div className="absolute top-4 left-4 right-4 z-50 max-h-[80vh] overflow-y-auto scroll-container">
                            <div
                                className="rounded-lg shadow-lg border"
                                style={{
                                    "backgroundColor": uiPalette.overlayCardBackgroundStrong,
                                    "borderColor":     uiPalette.overlayBorder,
                                    "color":           uiPalette.overlayTextPrimary,
                                }}
                            >
                                <div
                                    className="flex justify-between items-center p-2 border-b"
                                    style={{ "borderColor": uiPalette.overlayBorder }}
                                >
                                    <h3 className="font-bold text-lg">
🔧 Debug Boussole
                                    </h3>
                                    <button
                                        className="px-3 py-1 rounded text-sm font-semibold"
                                        onClick={() => setShowCompassDebug( false )}
                                        style={{
                                            "backgroundColor": uiPalette.selectorActiveBackgroundStrong,
                                            "color":           uiPalette.selectorActiveText,
                                        }}
                                    >
                                        Fermer
                                    </button>
                                </div>
                                <div className="p-2">
                                    <CompassDebug isDarkUi={isDarkUi} />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex flex-col h-full w-screen items-center">
                    {/* Floated Components */}
                    {/* Header elements */}
                    <Header isHome={true} />
                    {userLocation.longitude != null && userLocation.latitude != null && (
                        <>
                            <SearchBarInput
                                arrivalInput={arrivalInput}
                                arrivalSelectedAddress={arrivalSelectedAddress}
                                autoComplete={autocompleteDepartureValues}
                                input={departureInput}
                                inputRef={DepartureInputRef}
                                isArrival={false}
                                isDarkUi={isDarkUi}
                                isMerge={isMerge}
                                lat={userLocation.latitude}
                                lon={userLocation.longitude}
                                myPositionAutocomplete={myPositionAutocomplete}
                                onResetPanelExpanded={resetPanelExpanded}
                                onSwap={handleSwapDepartureArrival}
                                selectedAddress={departureSelectedAddress}
                                setAutocompleteValues={setAutocompleteDepartureValues}
                                setITakeMyCarData={setITakeMyCarData}
                                setInput={setDepartureInput}
                                setIsMerge={setIsMerge}
                                setSelectedAddress={setDepartureSelectedAddress}
                                setSelectedItinerary={setSelectedItinerary}
                            />
                            {/* Arrival search field */}
                            <SearchBarInput
                                autoComplete={autocompleteValues}
                                date={date}
                                departureInput={departureInput}
                                departureInputRef={DepartureInputRef}
                                departureSelectedAddress={departureSelectedAddress}
                                input={arrivalInput}
                                inputRef={ArrivalInputRef}
                                isArrival={true}
                                isDarkUi={isDarkUi}
                                isEditedDate={isEditedDate}
                                isMerge={isMerge}
                                lat={userLocation.latitude}
                                lon={userLocation.longitude}
                                myPositionAutocomplete={myPositionAutocomplete}
                                onResetPanelExpanded={resetPanelExpanded}
                                onSwap={handleSwapDepartureArrival}
                                position={arrivalFieldTopPx}
                                selectedAddress={arrivalSelectedAddress}
                                setAutocompleteDepartureValues={
                                    setAutocompleteDepartureValues
                                }
                                setAutocompleteValues={setAutocompleteValues}
                                setBackupReturnItineraries={setBackupReturnItineraries}
                                setDate={setDate}
                                setDepartureInput={setDepartureInput}
                                setDepartureSelectedAddress={setDepartureSelectedAddress}
                                setGoItineraries={setGoItineraries}
                                setITakeMyCarData={setITakeMyCarData}
                                setInput={setArrivalInput}
                                setIsEditedDate={setIsEditedDate}
                                setIsMerge={setIsMerge}
                                setItineraries={setCarItineraries}
                                setItineraryListContentMode={setItineraryListContentMode}
                                setSelectedAddress={setArrivalSelectedAddress}
                                setSelectedCarRouteId={setSelectedCarRouteId}
                                setSelectedItinerary={setSelectedItinerary}
                                setShowItineraryPanel={setVisible}
                                setVisible={setVisible}
                                visible={visible}
                            />
                            <FloatingMenu
                                appearanceMode={mapAppearanceMode}
                                defaultStyleId={defaultMapStyleId}
                                dynamicIslandSlotProps={dynamicIsland.slotProps}
                                futureSavedItineraryCount={futureSavedItineraryCount}
                                isDarkUi={isDarkUi}
                                isSearchAutocompleteOpen={isSearchAutocompleteOpen}
                                itineraryPanelHeight={itineraryPanelHeightPx}
                                onAppearanceModeChange={setMapAppearanceMode}
                                onNavigate={handleNavigate}
                                onOpenSavedItineraries={handleOpenSavedItineraries}
                                onPricingPrefsPanelOpenStateChange={handlePricingPrefsPanelOpenStateChange}
                                onReturnToUserLocation={returnToUserLocation}
                                onStyleOverrideChange={setMapStyleOverride}
                                onTransportsPrefsPanelOpenStateChange={handleTransportsPrefsPanelOpenStateChange}
                                searchBarsBottomPx={searchBarsBottomPx}
                                styleOverride={mapStyleOverride}
                            />
                            {/* Itinerary list */}
                            <div
                                className="absolute bottom-0 z-10 md:w-3/5 w-full"
                                id="itinerary-panel-root"
                            >
                                {/* FLoating bottom icons */}
                                {/* End fLoating bottom icons */}
                                <ItineraryPanel
                                    isDarkUi={isDarkUi}
                                    visible={visible}
                                >
                                    <div
                                        className={`flex flex-col min-h-0 ${ panelContainerTransitionClass }`}
                                        id="itinerary-panel-container"
                                        ref={containerRef as React.Ref<HTMLDivElement>}
                                        style={{
                                            "maxHeight": `${ effectivePanelHeightVh }dvh`,
                                            ...shouldLockPanelHeightToSnap
                                                ? {
                                                    "minHeight": `${ effectivePanelHeightVh }dvh`,
                                                    "height":    `${ effectivePanelHeightVh }dvh`,
                                                }
                                                : { "height": isDragging ? `${ effectivePanelHeightVh }dvh` : undefined },
                                            "overflow": "hidden",
                                        }}
                                    >
                                        <ItineraryList
                                            ITakeMyCar={ITakeMyCar}
                                            ITakeMyCarData={ITakeMyCarData}
                                            activeSavedFilterTab={activeSavedFilterTab}
                                            backupReturnItineraries={backupReturnItineraries}
                                            carItineraries={carItineraries}
                                            date={date}
                                            debugItineraries={debugItineraries}
                                            departure={{
                                                "lat": userLocation.latitude,
                                                "lon": userLocation.longitude,
                                            }}
                                            departureSelectedAddress={departureSelectedAddress}
                                            focusMapOnPoint={focusMapOnPoint}
                                            goItineraries={goItineraries}
                                            handleResetMap={handleResetMap}
                                            isDarkUi={isDarkUi}
                                            isEditedDate={isEditedDate}
                                            isEuro={isEuro}
                                            isMerge={isMerge}
                                            isModalOpen={isModalOpen}
                                            isPricingPrefsPanelOpen={isPricingPrefsPanelOpen}
                                            itineraries={itineraries}
                                            itineraryListContentMode={itineraryListContentMode}
                                            itineraryListHeight={effectivePanelHeightVh}
                                            navigate={handleNavigate}
                                            onCloseSearchResults={handleCloseSearchResults}
                                            onResetPanelExpanded={resetPanelExpanded}
                                            onRestoreItineraryDetailsPanelSnap={restoreItineraryDetailsPanelSnap}
                                            onRestoreItineraryListPanelSnap={restoreItineraryListPanelSnap}
                                            panelResizeLayout={panelResizeLayout}
                                            selectedAddress={arrivalSelectedAddress}
                                            selectedCarRouteId={selectedCarRouteId}
                                            selectedInputs={selectedInputs}
                                            selectedItinerary={selectedItinerary}
                                            selectedLeg={selectedLeg}
                                            setActiveSavedFilterTab={setActiveSavedFilterTab}
                                            setBackupReturnItineraries={setBackupReturnItineraries}
                                            setCarItineraries={setCarItineraries}
                                            setDate={setDate}
                                            setGoItineraries={setGoItineraries}
                                            setITakeMyCar={setITakeMyCar}
                                            setITakeMyCarData={setITakeMyCarData}
                                            setIsEditedDate={setIsEditedDate}
                                            setIsMerge={setIsMerge}
                                            setItineraries={setItineraries}
                                            setItineraryListContentMode={setItineraryListContentMode}
                                            setSelectedCarRouteId={setSelectedCarRouteId}
                                            setSelectedItinerary={setSelectedItinerary}
                                            setSelectedLeg={setSelectedLegForTransitPath}
                                            setShowArrowTop={setShowArrowTop}
                                            setVisible={setVisible}
                                            visible={visible}
                                        />
                                    </div>
                                </ItineraryPanel>
                            </div>
                        </>
                    )}
                    {/* Debug overlay removed - no longer needed */}
                </div>
            </IonContent>
        </IonPage>
    );
};

export default Home;
